// Patient documents library service (Phase 8 §3.5, PAT-09)
// File: src/services/patientmr/patientDocuments.service.ts

import { apiClient } from '../api-client';
import { ApiResponse } from '../../dtos/common/api.dto';
import type {
  AdmissionPacket,
  CreatePatientDocumentRequest,
  ListPatientDocumentsResponse,
  PatientDocument,
  PatientDocumentMetadata,
  PatientDocumentUrlResponse,
  SignAdmissionDocumentRequest,
} from '../../dtos/patientmr/patientDocuments.dto';
import { PresignedUploadService, ticketRequestFor } from '../uploads/presignedUpload';
import type { UploadTicket } from '../uploads/presignedUpload';

export class PatientDocumentsService {
  /** POST …/documents/upload-url → pre-signed POST ticket */
  static async getUploadTicket(patientId: string, file: File): Promise<UploadTicket> {
    const response: ApiResponse<UploadTicket> = await apiClient.post(
      `/patientmr/${patientId}/documents/upload-url`,
      ticketRequestFor(file)
    );
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error?.message || 'Failed to get an upload link');
  }

  /** POST …/documents — confirm the uploaded object and create the library entry */
  static async create(patientId: string, body: CreatePatientDocumentRequest): Promise<PatientDocument> {
    const response: ApiResponse<{ document?: PatientDocument } & Partial<PatientDocument>> = await apiClient.post(
      `/patientmr/${patientId}/documents`,
      body
    );
    if (response.success && response.data) {
      return (response.data.document ?? response.data) as PatientDocument;
    }
    throw new Error(response.error?.message || 'Failed to save the document');
  }

  /** Upload a file and add it to the library (ticket → S3 → confirm). */
  static async upload(
    patientId: string,
    file: File,
    metadata: PatientDocumentMetadata,
    onProgress?: (pct: number) => void
  ): Promise<PatientDocument> {
    return PresignedUploadService.uploadAndConfirm(
      file,
      () => PatientDocumentsService.getUploadTicket(patientId, file),
      (key) => PatientDocumentsService.create(patientId, {
        key,
        documentName: file.name,
        type: metadata.type,
        documentDate: metadata.documentDate,
        title: metadata.title,
        ...(metadata.signerName ? { signerName: metadata.signerName } : {}),
        ...(metadata.signerRelationship ? { signerRelationship: metadata.signerRelationship } : {}),
        ...(metadata.service ? { service: metadata.service } : {}),
      }),
      onProgress
    );
  }

  /** GET …/documents (soft-deleted documents are not returned) */
  static async list(patientId: string): Promise<PatientDocument[]> {
    const response: ApiResponse<ListPatientDocumentsResponse> = await apiClient.get(`/patientmr/${patientId}/documents`);
    if (response.success && response.data) {
      return response.data.documents || [];
    }
    throw new Error(response.error?.message || 'Failed to load documents');
  }

  /** GET …/documents?view=packet — admission packet templates, required items and signed status (Phase 19 F18) */
  static async getAdmissionPacket(patientId: string): Promise<AdmissionPacket> {
    const response: ApiResponse<AdmissionPacket> = await apiClient.get(`/patientmr/${patientId}/documents`, { view: 'packet' });
    if (response.success && response.data) {
      return {
        templates: response.data.templates || {},
        items: response.data.items || [],
        complete: response.data.complete === true,
        guardians: response.data.guardians || [],
      };
    }
    throw new Error(response.error?.message || 'Failed to load the admission packet');
  }

  /**
   * POST …/documents { mode: 'SIGN' } — sign a packet document in Pelora. The server checks the
   * signer's authority (403 SIGNER_NOT_AUTHORIZED), records the logged-in user as the witness and
   * renders the PDF. Non-2xx responses throw ApiError (domainCode carries the error code).
   */
  static async signAdmissionDocument(patientId: string, body: SignAdmissionDocumentRequest): Promise<PatientDocument> {
    const response: ApiResponse<{ document?: PatientDocument } & Partial<PatientDocument>> = await apiClient.post(
      `/patientmr/${patientId}/documents`,
      body
    );
    if (response.success && response.data) {
      return (response.data.document ?? response.data) as PatientDocument;
    }
    throw new Error(response.error?.message || 'The document was not signed. Try again.');
  }

  /** GET …/documents/{id}/url → short-lived signed GET URL (download is audited server-side) */
  static async getDownloadUrl(patientId: string, documentId: string): Promise<string> {
    const response: ApiResponse<PatientDocumentUrlResponse> = await apiClient.get(
      `/patientmr/${patientId}/documents/${documentId}/url`
    );
    if (response.success && response.data?.url) {
      return response.data.url;
    }
    throw new Error(response.error?.message || 'Failed to get a download link');
  }

  /** DELETE …/documents/{id} — soft delete (the file is kept) */
  static async remove(patientId: string, documentId: string): Promise<void> {
    const response: ApiResponse<unknown> = await apiClient.delete(`/patientmr/${patientId}/documents/${documentId}`);
    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to delete the document');
    }
  }
}

export default PatientDocumentsService;
